import { Stack, ViewportType, useViewport } from '@amx/ux-twoui';
import { classNamesFunction } from '@fluentui/react';
import React, { useEffect, useState } from 'react';
import type { RouteProps } from 'react-router-dom';

import { type ILayoutStyles, layoutStyles } from './Layout.styles';
import { LeftNav } from '../left-nav/LeftNav';
import { NAVIGATION_LINKS, MOBILE_BREAK_POINT } from '../../shared/constants';
import { isLeftNavInvisible } from '../../shared/utils';
import { ConnectedHeader } from '../../Header';
import { ConsentHeader } from '../../TermsOfUse/Consent/ConsentHeader';
import { useLocalization } from '@microsoft/portal-app/lib/shared/localization';

const getClassNames = classNamesFunction<Record<string, never>, ILayoutStyles>();
const classNames = getClassNames(layoutStyles);
export const Layout: React.FC<RouteProps> = ({ location, children }) => {
  const [selectedNavLinkKey, setSelectedNavLinkKey] = useState<string | undefined>(NAVIGATION_LINKS.overview.key);

  useEffect(() => {
    const path = location?.pathname ?? '/';
    const mainPath = `/${path.split('/')[1]}`;

    switch (mainPath) {
      case '':
      case NAVIGATION_LINKS.overview.link:
        setSelectedNavLinkKey(NAVIGATION_LINKS.overview.key);
        break;
      case NAVIGATION_LINKS.devices.link:
        setSelectedNavLinkKey(NAVIGATION_LINKS.devices.key);
        break;
      case NAVIGATION_LINKS.organizations.link:
        setSelectedNavLinkKey(NAVIGATION_LINKS.organizations.key);
        break;
      case NAVIGATION_LINKS.settingsAndPrivacy.link:
        setSelectedNavLinkKey(NAVIGATION_LINKS.settingsAndPrivacy.key);
        break;
      default:
        setSelectedNavLinkKey(undefined);
    }
  }, [location?.pathname]);

  const viewportType = useViewport();

  if (viewportType === MOBILE_BREAK_POINT) {
    return (
      <Stack horizontal={false}>
        <Header />
        <LeftNavContent selectedKey={selectedNavLinkKey} />
        <LayoutContent viewportType={viewportType}>{children}</LayoutContent>
      </Stack>
    );
  } else {
    return (
      <Stack horizontal={false} width={'100%'}>
        <Header />
        <Stack horizontal={true} horizontalAlign="start">
          {selectedNavLinkKey && (
            <div className={classNames.leftNav}>
              <LeftNavContent selectedKey={selectedNavLinkKey} />
            </div>
          )}
          <LayoutContent viewportType={viewportType}>{children}</LayoutContent>
        </Stack>
      </Stack>
    );
  }
};

const Header: React.FC = () => {
  const [t] = useLocalization('Account');

  const isTermsOfUseConsentPage = NAVIGATION_LINKS.termsOfUse.subRoutePaths!.consent.link === window.location.pathname;

  return isTermsOfUseConsentPage ? <ConsentHeader appTitle={t('My Account')} /> : <ConnectedHeader />;
};

const LeftNavContent: React.FC<{ selectedKey?: string }> = ({ selectedKey }) => {
  return selectedKey && !isLeftNavInvisible() ? <LeftNav selectedNavLinkKey={selectedKey} /> : null;
};

const LayoutContent: React.FC<{
  viewportType: ViewportType;
  children?: ((props: { [K: string]: string | undefined }) => React.ReactNode) | React.ReactNode | undefined;
}> = ({ viewportType, children }) => {
  return (
    <div className={classNames.pageContent} id={'main-content'} tabIndex={-1}>
      <div
        id="aria-live-region"
        role="status"
        aria-live="polite"
        style={{ position: 'absolute', left: '-9999px', height: '1px', width: '1px', overflow: 'hidden' }}
      />
      <div className={viewportType === MOBILE_BREAK_POINT ? classNames.mobileContent : classNames.content}>
        {children}
      </div>
    </div>
  );
};
